/* eslint-disable @typescript-eslint/no-empty-function */
import { ChangeDetectionStrategy, Component, computed, forwardRef, input, signal, viewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatIconModule } from '@angular/material/icon';
import { SatPopover, SatPopoverModule } from '@zerops/zef/popover';
import { ZefFormsModule } from '@zerops/zef/forms';
import { getMaxAssignableProjectRole } from '@zerops/zef/permission';
import { Boxed } from 'ngrx-forms';
import { ClientUser, ClientUserRole } from '@zerops/models/client-user';
import { AssignableProjectRole, Project, ProjectRole } from '@zerops/models/project';
import { IntegrationTokenProjectAccess, IntegrationTokenRoleCode } from '@zerops/models/integration-token';
import { ProjectChipComponent } from '@zerops/zui/project-chip';
import { AddProjectAccessPopoverComponent } from '@zerops/zui/add-project-access-popover';

interface MenuAction {
  type: 'toggle-role' | 'remove';
  label: string;
  icon?: string;
}

interface ProjectChipData {
  projectId: string;
  projectName: string;
  tags: string[];
  roleCode: ProjectRole;
  canToggleRole: boolean;
  maxAssignableRole: AssignableProjectRole | null;
  hasChanged: boolean;
  menuActions: MenuAction[];
}

@Component({
  selector: 'zui-project-access-override',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ProjectAccessOverrideComponent),
      multi: true
    }
  ],
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    SatPopoverModule,
    ZefFormsModule,
    ProjectChipComponent,
    AddProjectAccessPopoverComponent
  ],
  templateUrl: './project-access-override.component.html',
  styleUrl: './project-access-override.component.scss'
})
export class ProjectAccessOverrideComponent implements ControlValueAccessor {
  // # Inputs
  availableProjects = input.required<Project[]>();
  currentUserRole = input.required<string>();
  currentUser = input.required<ClientUser>();
  tokenPrimaryRole = input<IntegrationTokenRoleCode | null>(null);
  disabled = input<boolean>(false);
  changesMap = input<Map<string, boolean>>(new Map());
  addButtonText = input<string>('Set custom accesses');

  // # View Queries
  addAccessPopover = viewChild(SatPopover);
  addAccessPopoverRef = viewChild(AddProjectAccessPopoverComponent);

  // # Internal State
  value = signal<IntegrationTokenProjectAccess[]>([]);
  isDisabled = signal<boolean>(false);

  // # ControlValueAccessor
  onChange: (value: IntegrationTokenProjectAccess[]) => void = () => {};
  onTouched: () => void = () => {};

  writeValue(value: Boxed<IntegrationTokenProjectAccess[]> | IntegrationTokenProjectAccess[] | null) {
    // Handle both boxed (from ngrx-forms) and plain array values
    let unwrapped: IntegrationTokenProjectAccess[];

    if (value && typeof value === 'object' && 'value' in value) {
      // Boxed value from ngrx-forms
      unwrapped = value.value;
    } else if (Array.isArray(value)) {
      // Plain array
      unwrapped = value;
    } else {
      // null or undefined
      unwrapped = [];
    }

    this.value.set(unwrapped);
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.isDisabled.set(isDisabled);
  }

  // # Computed
  projectAccessMap = computed(() => {
    return this.value().reduce((map, access) => {
      map[access.projectId] = access.roleCode;
      return map;
    }, {} as Record<string, ProjectRole>);
  });

  availableToAdd = computed(() => {
    const accessMap = this.projectAccessMap();
    return this.availableProjects().filter(p => !accessMap[p.id]);
  });

  allChips = computed<ProjectChipData[]>(() => {
    const projects = this.availableProjects();
    const projectMap = projects.reduce((map, p) => {
      map[p.id] = p;
      return map;
    }, {} as Record<string, Project>);

    const changes = this.changesMap();

    return this.value().map(access => {
      const project = projectMap[access.projectId];
      if (!project) return null;

      const maxAssignableRole = this.#getMaxAssignableRole(project);
      const canToggleRole = this.#canToggleRole(access.roleCode, maxAssignableRole);

      const menuActions: MenuAction[] = [];

      if (canToggleRole) {
        menuActions.push({
          type: 'toggle-role',
          label: access.roleCode === ClientUserRole.Admin ? 'Make read only' : 'Give full access',
          icon: access.roleCode === ClientUserRole.Admin ? 'visibility' : 'lock_open'
        });
      }

      menuActions.push({
        type: 'remove',
        label: 'Remove',
        icon: 'close'
      });

      return {
        projectId: project.id,
        projectName: project.name,
        tags: project.tagList || [],
        roleCode: access.roleCode,
        canToggleRole,
        maxAssignableRole,
        hasChanged: changes.has(project.id),
        menuActions
      };
    }).filter(Boolean) as ProjectChipData[];
  });

  // # Methods
  #getMaxAssignableRole(project: Project): AssignableProjectRole | null {
    return getMaxAssignableProjectRole(project, this.currentUser());
  }

  #canToggleRole(currentRole: ProjectRole, maxRole: AssignableProjectRole | null): boolean {
    if (!maxRole) return false;
    if (maxRole === ClientUserRole.ReadOnly) return false; // Can only assign READ_ONLY
    return true; // maxRole is ADMIN, can toggle between ADMIN/READ_ONLY
  }

  onAddAccessConfirmed(selections: Array<{ projectId: string; roleCode: AssignableProjectRole; project: Project }>) {
    const current = this.value();
    const newAccesses = selections.map(s => ({
      projectId: s.projectId,
      roleCode: s.roleCode
    }));

    const updated = [ ...current, ...newAccesses ];
    this.value.set(updated);
    this.onChange(updated);
    this.onTouched();

    // Close popover
    const popover = this.addAccessPopover();
    if (popover) {
      popover.close();
    }
  }

  onMenuAction(projectId: string, action: MenuAction) {
    if (action.type === 'toggle-role') {
      this.#toggleRole(projectId);
    } else if (action.type === 'remove') {
      this.#removeProject(projectId);
    }
  }

  #toggleRole(projectId: string) {
    const current = this.value();
    const updated = current.map(access => {
      if (access.projectId === projectId) {
        const newRole: ProjectRole = access.roleCode === ClientUserRole.Admin ? ClientUserRole.ReadOnly : ClientUserRole.Admin;
        return { ...access, roleCode: newRole };
      }
      return access;
    });

    this.value.set(updated);
    this.onChange(updated);
    this.onTouched();
  }

  #removeProject(projectId: string) {
    const current = this.value();
    const updated = current.filter(access => access.projectId !== projectId);

    this.value.set(updated);
    this.onChange(updated);
    this.onTouched();
  }
}
